import analyticsService from "../AnalyticsService";
import { ANALYTICS_EVENTS, ENGAGEMENT_MIN_MS } from "../constants";

// GA4 reserves `user_engagement`, so per-page engagement is reported with the
// custom `page_engagement` event. gtag.js attaches its own
// `engagement_time_msec` to it, attributing the foreground time accrued since
// the previous hit to the page being left instead of the next route.
const isDocumentVisible = () =>
  globalThis.document?.visibilityState !== "hidden";

export const ENGAGEMENT_REASONS = {
  NAVIGATION: "navigation",
  HIDDEN: "hidden",
  UNLOAD: "unload",
  USER_CHANGE: "user_change",
};

export class EngagementTracker {
  page = null;

  activeSince = null;

  accumulatedMs = 0;

  focused = true;

  listenersAttached = false;

  constructor(now = () => Date.now()) {
    this.now = now;
  }

  isActive() {
    return isDocumentVisible() && this.focused;
  }

  pause() {
    if (this.activeSince === null) return;
    this.accumulatedMs += Math.max(0, this.now() - this.activeSince);
    this.activeSince = null;
  }

  resume() {
    if (!this.page || this.activeSince !== null || !this.isActive()) return;
    this.activeSince = this.now();
  }

  getEngagedMs() {
    const running =
      this.activeSince === null ? 0 : Math.max(0, this.now() - this.activeSince);
    return this.accumulatedMs + running;
  }

  startPage(pageParams = {}) {
    this.attachListeners();
    this.flush(ENGAGEMENT_REASONS.NAVIGATION, {
      next_page_title: pageParams.page_title,
      next_page_location: pageParams.page_location,
    });
    this.page = {
      page_location: pageParams.page_location,
      page_title: pageParams.page_title,
      page_category: pageParams.page_category,
      dashboard: pageParams.dashboard ?? null,
    };
    this.accumulatedMs = 0;
    this.activeSince = null;
    this.resume();
  }

  flush(reason, nextPage = {}) {
    if (!this.page) return;

    this.pause();
    const engagedMs = Math.round(this.accumulatedMs);
    this.accumulatedMs = 0;

    if (engagedMs >= ENGAGEMENT_MIN_MS) {
      analyticsService.track(
        ANALYTICS_EVENTS.PAGE_ENGAGEMENT,
        {
          ...this.page,
          next_page_title: nextPage.next_page_title ?? null,
          next_page_location: nextPage.next_page_location ?? null,
          engaged_time_msec: engagedMs,
          engagement_reason: reason,
        },
        { dedupe: false },
      );
    }

    if (reason !== ENGAGEMENT_REASONS.NAVIGATION) this.resume();
  }

  handleVisibilityChange = () => {
    if (isDocumentVisible()) {
      this.resume();
      return;
    }
    this.flush(ENGAGEMENT_REASONS.HIDDEN);
  };

  handlePageHide = () => {
    this.flush(ENGAGEMENT_REASONS.UNLOAD);
  };

  handleBlur = () => {
    // Focus moving into an embedded iframe is still engagement with this page;
    // the check is deferred because activeElement updates after `blur`.
    globalThis.setTimeout?.(() => {
      const doc = globalThis.document;
      if (doc?.activeElement?.tagName === "IFRAME" || doc?.hasFocus?.()) {
        return;
      }
      this.focused = false;
      this.pause();
    }, 0);
  };

  handleFocus = () => {
    this.focused = true;
    this.resume();
  };

  attachListeners() {
    if (this.listenersAttached || !globalThis.addEventListener) return;
    globalThis.document?.addEventListener?.(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    globalThis.addEventListener("pagehide", this.handlePageHide);
    globalThis.addEventListener("blur", this.handleBlur);
    globalThis.addEventListener("focus", this.handleFocus);
    this.listenersAttached = true;
  }

  detachListeners() {
    if (!this.listenersAttached) return;
    globalThis.document?.removeEventListener?.(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    globalThis.removeEventListener?.("pagehide", this.handlePageHide);
    globalThis.removeEventListener?.("blur", this.handleBlur);
    globalThis.removeEventListener?.("focus", this.handleFocus);
    this.listenersAttached = false;
  }

  reset() {
    this.detachListeners();
    this.page = null;
    this.activeSince = null;
    this.accumulatedMs = 0;
    this.focused = true;
  }
}

const engagementTracker = new EngagementTracker();

export default engagementTracker;
